import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

// API Configuration - Hardcoded to ensure correct port
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api/v1';
const API_TIMEOUT = 30000; // 30 seconds

// Token storage keys
const ACCESS_TOKEN_KEY = 'nova_access_token';
const REFRESH_TOKEN_KEY = 'nova_refresh_token';

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface ApiError {
  success: false;
  message: string;
  code?: string;
  details?: any[];
}

// Request retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Token management utilities
export const tokenStorage = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  
  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  
  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  
  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
  
  hasValidToken: (): boolean => {
    const token = tokenStorage.getAccessToken();
    if (!token) return false;
    
    try {
      // Check if token is expired (basic check)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      // Add 30 second buffer for token expiration
      const isValid = payload.exp > (currentTime + 30);
      
      if (!isValid) {
        console.log('Token is expired or will expire soon');
      }
      
      return isValid;
    } catch (error) {
      console.log('Error validating token:', error);
      return false;
    }
  }
};

// Create the main API client
class ApiClient {
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = tokenStorage.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Log request in development
        if (import.meta.env.DEV) {
          console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
            headers: config.headers,
            data: config.data,
          });
        }

        return config;
      },
      (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle auth and errors
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        // Log response in development
        if (import.meta.env.DEV) {
          console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            data: response.data,
          });
        }

        return response;
      },
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Log error in development
        if (import.meta.env.DEV) {
          console.error(`❌ API Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`, {
            status: error.response?.status,
            data: error.response?.data,
          });
        }

        // Handle 401 Unauthorized - Token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newToken = await this.refreshAccessToken();
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.axiosInstance(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.handleAuthFailure();
            return Promise.reject(refreshError);
          }
        }

        // Handle network errors with retry
        if (this.shouldRetry(error) && this.getRetryCount(originalRequest) < MAX_RETRIES) {
          return this.retryRequest(originalRequest);
        }

        return Promise.reject(this.normalizeError(error));
      }
    );
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh(refreshToken);

    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(refreshToken: string): Promise<string> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refreshToken,
      });

      const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;
      tokenStorage.setTokens(accessToken, newRefreshToken);
      
      return accessToken;
    } catch (error) {
      tokenStorage.clearTokens();
      throw error;
    }
  }

  private handleAuthFailure(): void {
    tokenStorage.clearTokens();
    
    // Dispatch custom event for auth failure
    window.dispatchEvent(new CustomEvent('auth:failure'));
    
    // Redirect to login if not already there
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  private shouldRetry(error: AxiosError): boolean {
    // Retry on network errors or 5xx server errors
    return !error.response || (error.response.status >= 500 && error.response.status < 600);
  }

  private getRetryCount(config: AxiosRequestConfig & { _retryCount?: number }): number {
    return config._retryCount || 0;
  }

  private async retryRequest(config: AxiosRequestConfig & { _retryCount?: number }): Promise<AxiosResponse> {
    config._retryCount = this.getRetryCount(config) + 1;
    
    // Exponential backoff
    const delay = RETRY_DELAY * Math.pow(2, config._retryCount - 1);
    await new Promise(resolve => setTimeout(resolve, delay));
    
    console.log(`🔄 Retrying request (${config._retryCount}/${MAX_RETRIES}): ${config.method?.toUpperCase()} ${config.url}`);
    
    return this.axiosInstance(config);
  }

  private normalizeError(error: AxiosError<ApiError>): Error {
    if (error.response?.data) {
      const apiError = error.response.data;
      const errorMessage = apiError.message || 'An unexpected error occurred';
      const customError = new Error(errorMessage) as Error & { 
        code?: string; 
        status?: number; 
        details?: any[] 
      };
      
      customError.code = apiError.code;
      customError.status = error.response.status;
      customError.details = apiError.details;
      
      return customError;
    }

    if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
      return new Error('Network connection failed. Please check your internet connection.');
    }

    if (error.code === 'ECONNABORTED') {
      return new Error('Request timeout. Please try again.');
    }

    return new Error(error.message || 'An unexpected error occurred');
  }

  // HTTP Methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  // Upload file with progress
  async uploadFile<T = any>(
    url: string, 
    file: File, 
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.axiosInstance.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Get raw axios instance for advanced usage
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Create and export the API client instance
export const apiClient = new ApiClient();

// Export individual methods for convenience
export const { get, post, put, patch, delete: del, uploadFile } = apiClient;

// Auth Types
export interface LoginRequest {
  /** `name@restaurantCode`, composed by the sign-in page. */
  login: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  /** Short name only — the server appends the restaurant's code. */
  name: string;
  password: string;
  role?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface AuthResponse {
  user: any;
  /** The restaurant behind the login's code, so the device can remember it. */
  restaurant?: { id?: string; code: string; name: string; isPlatform?: boolean } | null;
  accessToken: string;
  refreshToken: string;
}

// Auth Service - Real API implementation
export const AuthService = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials);
      const { user, tokens, restaurant } = response.data.data;
      return {
        user,
        restaurant: restaurant ?? null,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      };
    } catch (error: any) {
      // Handle specific error response format from backend
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      }
      
      // Handle network errors
      if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        // Note: In a real app, we'd use i18n here, but for now we'll use fallback messages
        throw new Error('Unable to connect to the server. Please check your connection and try again.');
      }
      
      // Generic fallback
      throw new Error('Login failed. Please check your credentials and try again.');
    }
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/register', userData);
    return response.data;
  },

  async changePassword(passwords: ChangePasswordRequest): Promise<void> {
    await apiClient.put('/auth/change-password', {
      currentPassword: passwords.currentPassword,
      newPassword: passwords.newPassword,
    });
  },

  /*
   * forgotPassword and resetPassword used to live here as setTimeout stubs that
   * resolved without touching the network, and the sign-in page's «Забыли
   * пароль?» link called them — so it reported success and did nothing.
   *
   * They are gone rather than implemented. A self-service reset is delivered by
   * email, which is not a channel anyone in this market reads. Recovery runs
   * through a person who already has a working endpoint: staff ask their
   * manager, an owner asks Nova support.
   */

  async validateToken(): Promise<boolean> {
    // Mock implementation
    return new Promise((resolve) => {
      setTimeout(() => {
        const token = tokenStorage.getAccessToken();
        resolve(!!token);
      }, 500);
    });
  },

  async refreshToken(refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> {
    const response = await apiClient.post<{ accessToken: string; refreshToken: string }>('/auth/refresh', { refreshToken });
    return response.data;
  },
};

// User Service - Real API implementation
export const UserService = {
  async getProfile(): Promise<any> {
    const response = await apiClient.get('/users/profile');
    return response.data.user;
  },
};